// The "this tab just changed something" signal (#460).
//
// A window event an app's own api clients dispatch when one of their writes
// succeeded, so a surface showing server-derived state can re-read the moment the
// change landed instead of on its next poll. The demo bar's journey is the first
// listener: every task on it is ticked by something the visitor does in the SPA
// they are looking at, and that SPA knows the exact moment the request succeeded.
// Same mechanism as `lumi:open-support` (apiError.ts) and the module chromes'
// `lumi:notifications-changed`.
//
// A HINT, NEVER DATA. The detail names the client, the method and the request URL
// and nothing from the response; a listener answers it by re-reading through its
// own authorized path. And it only covers THIS tab's own writes — a change made by
// the worker or by another user never raises it — so a listener keeps its poll as
// the backstop. The push-based path is #472.
//
// Lumi's writes (#1102) ride the same event: when the chat panel in this tab sees
// one of its runs succeed (the app-kit transport's `runSucceeded`), it dispatches
// with `lumi` set, naming the module, tool and entity the run reports. A run the
// person approved from another device or an MCP client is not seen here.

export const DATA_CHANGED_EVENT = 'lumi:data-changed';

export interface DataChangedDetail {
  /** Which of the app's clients wrote — a label for debugging, never a routing key. */
  client: string;
  /** Upper-case HTTP method of the write — `LUMI` for a Lumi run. */
  method: string;
  /** The request URL as the client issued it (relative to its base URL); empty for a Lumi run. */
  url: string;
  /** Set when the write was a Lumi run's (#1102), never for the app's own requests. */
  lumi?: LumiChange;
}

/** What a succeeded Lumi run says it touched; `null` where the run does not say. */
export interface LumiChange {
  /** The module whose tool ran (`ats`, `time`, `people`, …). */
  module: string | null;
  tool: string | null;
  entityType: string | null;
  entityId: string | null;
}

/** Tells this tab's listeners a Lumi run succeeded (#1102). */
export function dispatchLumiChanged(change: LumiChange): void {
  dispatchDataChanged({ client: 'lumi', method: 'LUMI', url: '', lumi: change });
}

export function dispatchDataChanged(detail: DataChangedDetail): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent<DataChangedDetail>(DATA_CHANGED_EVENT, { detail }));
}

/** Subscribe; returns the unsubscribe. */
export function onDataChanged(handler: (detail: DataChangedDetail) => void): () => void {
  if (typeof window === 'undefined') return () => {};
  const listener = (event: Event) => handler((event as CustomEvent<DataChangedDetail>).detail);
  window.addEventListener(DATA_CHANGED_EVENT, listener);
  return () => window.removeEventListener(DATA_CHANGED_EVENT, listener);
}

// A read changes nothing, so it must not wake a listener — and a listener answers
// with a read, so a signal raised by reads could feed itself.
const READ_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

/**
 * The success-side response interceptor: dispatches for a 2xx answer to anything
 * but a read. Checks the status itself rather than trusting axios's default
 * `validateStatus`, which a single request may widen.
 */
export function dataChangedOnSuccess(client: string) {
  return <T extends { status?: number; config?: { method?: string; url?: string } }>(response: T): T => {
    const status = response?.status ?? 0;
    const method = String(response?.config?.method ?? 'get').toUpperCase();
    if (status >= 200 && status < 300 && !READ_METHODS.has(method)) {
      dispatchDataChanged({ client, method, url: response.config?.url ?? '' });
    }
    return response;
  };
}

/**
 * Installs the signal on an axios instance. Register it AFTER the instance's
 * HTML-response guard: axios runs response interceptors in registration order, so
 * a misrouted write answered with an HTML page is rejected before it gets here.
 */
export function installDataChangedSignal(
  instance: { interceptors: { response: { use: (onFulfilled: (r: any) => any) => number } } },
  client: string,
): void {
  instance.interceptors.response.use(dataChangedOnSuccess(client));
}
